// Guardrail: Veera surfaces descriptive observations only.
// Do NOT add scoring, evaluation, or behavior direction here.
// Coach-first rule: Outputs are for coach review only.
import { useEffect, useRef, useState } from "react";
import { ExternalLink, MoreHorizontal, Sparkles, ThumbsDown, ThumbsUp, Trash2, X } from "lucide-react";
import { CoachVeeraComposer } from "@/components/chat/CoachVeeraComposer";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useCoachVeeraDictation } from "@/hooks/useCoachVeeraDictation";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";
import { useCoachChat } from "@/hooks/useCoachChat";
import { trackCoachFeedback } from "@/lib/coachFeedback";
import {
  trackCoachVeeraPromptSelected,
  trackCoachVeeraQuestionSubmitted,
} from "@/lib/behavior/coachVeeraTelemetry";
import { ChatMessageBubble } from "@/components/chat/ChatMessageBubble";
import { toast } from "sonner";

interface CoachVeeraSidebarProps {
  pageContext?: {
    pageName: string;
    leaderId?: string;
    relationshipRevision?: string;
    data?: Record<string, unknown>;
  };
  className?: string;
  fullWidth?: boolean;
  onClose?: () => void;
  conversationDescription?: string;
  contextLabel?: string;
  composerDisabled?: boolean;
  lockedMessage?: string;
  onViewPlans?: () => void;
  surface?: "page" | "panel";
  onDraftStateChange?: (hasDraft: boolean) => void;
  isActive?: boolean;
}

const PAGE_SUGGESTIONS: Record<string, string[]> = {
  dashboard: [
    "Which leaders have recent moments to review?",
    "What patterns are showing up across recent moments?",
    "Any coach reviews waiting on me?",
  ],
  "coach-ai": [
    "What patterns am I missing?",
    "Prep me for my week",
    "Review my coaching patterns",
  ],
  default: [
    "What moments need review today?",
    "What patterns are emerging across leaders?",
    "Any areas missing recent context?",
  ],
};

export function CoachVeeraSidebar({
  pageContext,
  className,
  fullWidth,
  onClose,
  conversationDescription = "Portfolio coaching conversation",
  contextLabel,
  composerDisabled = false,
  lockedMessage,
  onViewPlans,
  surface = "page",
  onDraftStateChange,
  isActive = true,
}: CoachVeeraSidebarProps) {
  const { user } = useAuth();
  const [inputValue, setInputValue] = useState("");
  const [submittingFeedbackMessageId, setSubmittingFeedbackMessageId] = useState<string | null>(null);
  const [submittedFeedbackMessageIds, setSubmittedFeedbackMessageIds] = useState<Set<string>>(new Set());
  const feedbackIdBySourceRef = useRef(new Map<string, string>());
  const measuredSelectedLeaderAnswerIdsRef = useRef(new Set<string>());
  const pendingPromptSelectionRef = useRef<{
    coachId: string;
    scopeKey: string;
    message: string;
    promptIndex: number;
  } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const lastSubmittedMessageRef = useRef("");
  const errorAtSendRef = useRef<string | null>(null);
  const contextType = pageContext?.leaderId ? "leader" : "global";
  const measurementScopeKey = JSON.stringify([
    pageContext?.pageName ?? null,
    pageContext?.leaderId ?? null,
    pageContext?.data?.selectedLeaderIds ?? null,
  ]);
  const {
    messages,
    isLoading,
    isScopeReady = true,
    sendMessage,
    clearChat,
    messagesEndRef,
    isStreaming,
    streamingContent,
    error,
  } = useCoachChat(pageContext, contextType);
  const dictation = useCoachVeeraDictation({
    currentValue: inputValue,
    onValueChange: setInputValue,
  });

  const defaultSuggestions = PAGE_SUGGESTIONS[pageContext?.pageName || "default"] || PAGE_SUGGESTIONS.default;
  const contextualSuggestion = typeof pageContext?.data?.suggestedQuestion === "string"
    ? pageContext.data.suggestedQuestion.trim()
    : "";
  const suggestions = contextualSuggestion
    ? [contextualSuggestion, ...defaultSuggestions.filter((suggestion) => suggestion !== contextualSuggestion)]
    : defaultSuggestions;
  useEffect(() => {
    if (isActive) inputRef.current?.focus();
  }, [isActive]);

  useEffect(() => {
    onDraftStateChange?.(Boolean(inputValue.trim()));
  }, [inputValue, onDraftStateChange]);

  useEffect(() => {
    if (!user?.id) return;
    messages.forEach((message, index) => {
      if (
        message.role !== "assistant"
        || !message.feedbackSourceId
        || measuredSelectedLeaderAnswerIdsRef.current.has(message.feedbackSourceId)
      ) return;
      const precedingUserMessage = messages.slice(0, index).reverse()
        .find((candidate) => candidate.role === "user");
      if (!precedingUserMessage?.content.trim()) return;

      measuredSelectedLeaderAnswerIdsRef.current.add(message.feedbackSourceId);
      const pendingPrompt = pendingPromptSelectionRef.current;
      const promptIndex = pendingPrompt
        && pendingPrompt.coachId === user.id
        && pendingPrompt.scopeKey === measurementScopeKey
        && pendingPrompt.message === precedingUserMessage.content
        ? pendingPrompt.promptIndex
        : undefined;
      pendingPromptSelectionRef.current = null;
      if (!message.selectedLeaderContext) {
        return;
      }
      const hasExistingSession = messages.slice(0, index)
        .some((candidate) => candidate.role === "assistant" && Boolean(candidate.feedbackSourceId));
      void trackCoachVeeraQuestionSubmitted({
        coachId: user.id,
        leaderId: message.selectedLeaderContext.leaderId,
        surface: "coach_session_prep",
        question: precedingUserMessage.content,
        historyScope: "rolling_90d",
        hasExistingSession,
        responseId: message.feedbackSourceId,
      });
      if (promptIndex !== undefined) {
        void trackCoachVeeraPromptSelected({
          coachId: user.id,
          leaderId: message.selectedLeaderContext.leaderId,
          surface: "coach_session_prep",
          prompt: precedingUserMessage.content,
          promptIndex,
          responseId: message.feedbackSourceId,
        });
      }
    });
  }, [measurementScopeKey, messages, user?.id]);

  useEffect(() => {
    pendingPromptSelectionRef.current = null;
  }, [measurementScopeKey, user?.id]);

  useEffect(() => {
    if (!error) {
      errorAtSendRef.current = null;
      return;
    }
    if (error && error !== errorAtSendRef.current && !inputValue && lastSubmittedMessageRef.current) {
      setInputValue(lastSubmittedMessageRef.current);
    }
    pendingPromptSelectionRef.current = null;
  }, [error, inputValue]);

  const sendGuarded = (rawMessage: string, promptIndex?: number) => {
    const message = rawMessage.trim();
    if (!message || isLoading || !isScopeReady || composerDisabled) return;
    if (promptIndex !== undefined && user?.id) {
      pendingPromptSelectionRef.current = {
        coachId: user.id,
        scopeKey: measurementScopeKey,
        message,
        promptIndex,
      };
    }
    lastSubmittedMessageRef.current = message;
    errorAtSendRef.current = error;
    setInputValue("");
    void sendMessage(message);
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    sendGuarded(inputValue);
  };

  const handleClearChat = () => {
    pendingPromptSelectionRef.current = null;
    clearChat();
  };

  const submitAnswerFeedback = async (
    message: {
      id: string;
      feedbackSourceId?: string;
      coachInsight?: object | null;
      evidenceRefs?: Array<{ id: string; label: string; route: string | null }>;
    },
    usefulnessSignal: "helpful" | "not_helpful",
  ) => {
    if (
      !user?.id ||
      !message.feedbackSourceId ||
      submittingFeedbackMessageId ||
      submittedFeedbackMessageIds.has(message.id)
    ) return;
    setSubmittingFeedbackMessageId(message.id);
    try {
      const feedbackId = feedbackIdBySourceRef.current.get(message.feedbackSourceId) || crypto.randomUUID();
      feedbackIdBySourceRef.current.set(message.feedbackSourceId, feedbackId);
      const result = await trackCoachFeedback(user.id, undefined, {
        feedbackType: "coach_veera",
        sourceId: message.feedbackSourceId,
        sourceFunction: "coach-chat",
        aiOutput: { answer_present: true },
        action: usefulnessSignal === "helpful" ? "accept" : "dismiss",
        context: {
          surface: "coach_panel",
          role_side: "coach",
          intervention_type: "qa_response",
          usefulness_signal: usefulnessSignal,
          // Count only server-built exact refs; LLM-generated coachInsight hrefs
          // are never citation provenance.
          has_citations: Boolean(message.evidenceRefs?.length),
        },
        requireReadback: true,
        feedbackId,
      });
      if (!result.success) throw new Error(result.error || "Feedback readback failed");
      setSubmittedFeedbackMessageIds((current) => new Set(current).add(message.id));
      toast.success(usefulnessSignal === "helpful" ? "Marked helpful" : "Marked not helpful");
    } catch (feedbackError) {
      const messageText = feedbackError instanceof Error ? feedbackError.message : "Unknown error";
      toast.error(`Coach Veera feedback failed: ${messageText}`);
    } finally {
      setSubmittingFeedbackMessageId((current) => current === message.id ? null : current);
    }
  };

  return (
    <div className={cn("flex h-full flex-col bg-card", !fullWidth && "border-l border-border", className)}>
      <div
        data-testid="coach-veera-sidebar-header"
        className={cn(
          "flex items-center justify-between border-b border-border px-4 py-3",
          surface === "panel" && "bg-primary text-primary-foreground md:bg-card md:text-foreground",
        )}
      >
        <div className="flex items-center gap-2">
          <div className={cn(
            "flex h-8 w-8 items-center justify-center rounded-md bg-gradient-to-br from-violet-500 to-purple-600 text-white",
            surface === "panel" && "bg-none bg-white/15 md:bg-gradient-to-br",
          )}>
            <Sparkles className="h-4 w-4" />
          </div>
          <div>
            <h2 className={cn(
              "text-sm font-semibold text-foreground",
              surface === "panel" && "text-primary-foreground md:text-foreground",
            )}>Coach Veera</h2>
            <p className={cn(
              "text-xs text-muted-foreground",
              surface === "panel" && "text-primary-foreground/75 md:text-muted-foreground",
            )}>{conversationDescription}</p>
            {contextLabel ? <p className={cn(
              "text-xs font-medium text-violet-700",
              surface === "panel" && "text-violet-100 md:text-violet-700",
            )}>{contextLabel}</p> : null}
          </div>
        </div>
        <div className="flex items-center gap-1">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              aria-label="More Coach Veera options"
              className={cn(
                "h-8 w-8",
                surface === "panel" && "text-primary-foreground hover:bg-white/10 hover:text-white md:text-foreground md:hover:bg-accent md:hover:text-accent-foreground",
              )}
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={handleClearChat}
              disabled={
                !isScopeReady ||
                isLoading ||
                isStreaming ||
                messages.length === 0
              }
              className="gap-2"
            >
              <Trash2 className="h-4 w-4" />
              Clear conversation
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        {onClose ? (
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className={cn(
              "h-8 w-8",
              surface === "panel" && "text-primary-foreground hover:bg-white/10 hover:text-white md:text-foreground md:hover:bg-accent md:hover:text-accent-foreground",
            )}
            aria-label="Close Coach Veera"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </Button>
        ) : null}
        </div>
      </div>

      <ScrollArea className="min-h-0 flex-1 px-4">
        <div className="space-y-4 py-4">
          {messages.length === 0 && (
            <div className="flex flex-wrap gap-2">
              {suggestions.slice(0, 3).map((suggestion, suggestionIndex) => (
                <Button
                  key={suggestion}
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 rounded-full px-3 text-xs"
                  onClick={() => sendGuarded(suggestion, suggestionIndex)}
                  disabled={isLoading || !isScopeReady || composerDisabled}
                >
                  {suggestion}
                </Button>
              ))}
            </div>
          )}

          {messages.map((message, index) => {
            const feedbackSubmitted = submittedFeedbackMessageIds.has(message.id);
            const feedbackSubmitting = submittingFeedbackMessageId === message.id;
            return (
            <div key={message.id} className={cn("flex", message.role === "user" ? "justify-end" : "justify-start")}>
              <div className="max-w-[85%]">
                <ChatMessageBubble
                  content={message.content}
                  role={message.role}
                  showAssistantAvatar={message.role === "assistant" && (index === 0 || messages[index - 1]?.role !== "assistant")}
                  className={message.role === "user" ? "rounded-xl rounded-br-sm bg-violet-600 text-white" : "rounded-xl rounded-bl-sm"}
                />
                {message.role === "assistant" && message.selectedLeaderContext ? (
                  <div className="mt-2 space-y-1.5 pl-8 text-xs text-muted-foreground">
                    {message.selectedLeaderContext.evidenceStatus === "empty" ? (
                      <p>No indexed evidence was found for {message.selectedLeaderContext.leaderName}.</p>
                    ) : (
                      <p>Evidence context verified for {message.selectedLeaderContext.leaderName}.</p>
                    )}
                    {message.selectedLeaderContext.evidenceStatus === "degraded" ? (
                      <p>Some selected-leader evidence sources were unavailable.</p>
                    ) : null}
                    {message.evidenceRefs?.length ? (
                      <div aria-label="Answer sources" className="space-y-1">
                        {message.evidenceRefs.map((evidenceRef) => evidenceRef.route ? (
                          <a
                            key={evidenceRef.id}
                            href={evidenceRef.route}
                            className="flex items-start gap-1 text-violet-700 underline-offset-2 hover:underline dark:text-violet-300"
                          >
                            <ExternalLink className="mt-0.5 h-3 w-3 shrink-0" />
                            <span>{evidenceRef.label}</span>
                          </a>
                        ) : null)}
                      </div>
                    ) : message.selectedLeaderContext.evidenceStatus !== "empty" ? (
                      <p>Evidence was available, but no exact source routes could be linked.</p>
                    ) : null}
                  </div>
                ) : null}
                {message.role === "assistant" && message.feedbackSourceId ? (
                  <div className="mt-2 flex items-center gap-1 pl-8">
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7"
                      aria-label="Mark answer helpful"
                      disabled={feedbackSubmitting || feedbackSubmitted}
                      onClick={() => void submitAnswerFeedback(message, "helpful")}
                    >
                      <ThumbsUp className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7"
                      aria-label="Mark answer not helpful"
                      disabled={feedbackSubmitting || feedbackSubmitted}
                      onClick={() => void submitAnswerFeedback(message, "not_helpful")}
                    >
                      <ThumbsDown className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ) : null}
              </div>
            </div>
            );
          })}

          {isStreaming && (
            <div className="flex justify-start">
              <div className="max-w-[85%]">
                <ChatMessageBubble
                  content={streamingContent}
                  role="assistant"
                  showAssistantAvatar={messages.length === 0 || messages[messages.length - 1]?.role !== "assistant"}
                  className="rounded-xl rounded-bl-sm"
                />
              </div>
            </div>
          )}

          {isLoading && !isStreaming && (
            <div className="flex justify-start">
              <div className="rounded-xl bg-muted px-3 py-2">
                <div className="flex gap-1">
                  <span className="h-2 w-2 animate-bounce rounded-full bg-violet-400" style={{ animationDelay: "0ms" }} />
                  <span className="h-2 w-2 animate-bounce rounded-full bg-violet-400" style={{ animationDelay: "150ms" }} />
                  <span className="h-2 w-2 animate-bounce rounded-full bg-violet-400" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      <div className="border-t border-border p-3">
        {lockedMessage ? (
          <div className="mb-3 rounded-lg bg-violet-50 p-3 text-sm text-violet-950">
            <p>{lockedMessage}</p>
            {onViewPlans ? <Button type="button" variant="link" className="h-auto px-0 pt-2" onClick={onViewPlans}>View plans</Button> : null}
          </div>
        ) : null}
        {error ? (
          <div className="mb-3 text-sm text-destructive" role="status" aria-live="polite">
            <span>{error}{lastSubmittedMessageRef.current ? " Your message is ready to retry." : ""}</span>
          </div>
        ) : null}
        <CoachVeeraComposer
          value={inputValue}
          onValueChange={setInputValue}
          onSubmit={handleSubmit}
          placeholder="Message Coach Veera..."
          disabled={isLoading || !isScopeReady || composerDisabled}
          submitDisabled={!inputValue.trim() || isLoading || !isScopeReady || composerDisabled}
          inputRef={inputRef}
          controlsClassName={cn("items-center", surface === "panel" && "pb-[env(safe-area-inset-bottom)]")}
          inputClassName="h-10 rounded-xl"
          micButtonClassName="h-10 w-10"
          sendButtonClassName="h-10 w-10 bg-violet-600 hover:bg-violet-700"
          dictation={dictation}
        />
      </div>
    </div>
  );
}
